import React, { useContext, useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { cloneDeep } from 'lodash';
import { Modal, Select, Form, Button } from '@agconnections/grow-ui';
import GrowUIFormField from 'components/Forms/GrowUIFormField';
import { Formik } from 'formik';
import { useAuth } from 'utilities/base-auth';
import { timingKeyValuePair } from 'screens/helpers/constants';
import { Context } from 'components/Store';
import { AmplitudeContext } from 'utilities/amplitude/useAmplitude';
import useProductsData from 'hooks/useProductData';
import { FETCH_TYPES } from 'helpers/constants';
import usePopulateMapData from 'hooks/usePopulateMapData';
import { paths } from 'routes/paths';
import ConvertPlanTable from './components/ConvertPlanTable';
import { mapPlanToRec } from './helpers/dataHelpers';

const ConvertPlanToRecModal = ({ open, close, currentPlan = null }) => {
  const [mappedProducts, setMappedProducts] = useState([]);
  const allAreMarked = useMemo(() => {
    const numberShown = mappedProducts?.filter(p => !p.hide).length;
    const numberMarked = mappedProducts?.filter(p => !p.hide && p.marked)
      .length;
    return numberShown === numberMarked && numberShown > 0;
  }, [mappedProducts]);
  const { loading } = useProductsData();
  const history = useHistory();
  const [timings, setTimings] = useState(timingKeyValuePair);
  const [timingTags, setTimingTags] = useState([]);
  const [timingDropValue, setTimingDropValue] = useState(null);
  const [timingTagDropValue, setTimingTagDropValue] = useState(null);

  const { user } = useAuth();

  const [, dispatch] = useContext(Context);
  const {
    dataSorted: propertyLandingPageData,
    fieldsAndAreasGeoJSONCollection,
    reloadData
  } = usePopulateMapData({
    fetchType: FETCH_TYPES.propertiesMap
  });
  const { sendEventToAmplitude, events } = useContext(AmplitudeContext);

  const initialValues = {
    timing: '',
    timingTag: ''
  };

  const baseDropDownValues = [
    { key: 'clear', value: '- Clear Filter' },
    { key: 'none', value: 'None' }
  ];

  const resetMappedProducts = () => {
    const resetMapped = mappedProducts.map(prod => {
      const newProd = prod;
      newProd.hide = false;
      newProd.marked = false;
      return newProd;
    });
    setMappedProducts(resetMapped);
  };

  const closeModal = () => {
    setTimingDropValue(null);
    setTimingTagDropValue(null);
    resetMappedProducts();
    close();
  };

  const convertToRec = () => {
    const selectProducts = mappedProducts.filter(p => p.marked);

    // Track the initiate plan to rec event
    sendEventToAmplitude(events.epic.Plans.initiatePlanToRec, {
      'Product Count': selectProducts.length,
      'Plan ID': currentPlan?.id,
      'Plan Name': currentPlan?.name
    });

    const convertPlanToRec = mapPlanToRec(
      user,
      currentPlan,
      selectProducts,
      propertyLandingPageData,
      fieldsAndAreasGeoJSONCollection
    );

    dispatch({
      type: 'SET_CONVERT_PLAN',
      payload: convertPlanToRec
    });

    history.push(`${paths.recommendations}/convert`);
  };

  const markUnmarkAll = isMarked => {
    const updatedProducts = mappedProducts.map(prod => ({
      ...prod,
      marked: prod.hide ? prod.marked : isMarked
    }));
    setMappedProducts(updatedProducts);
  };

  const markUnmarkProduct = (isMarked, idx) => {
    const updatedProducts = mappedProducts.map(prod => ({
      ...prod,
      marked: prod.trackingId === idx ? isMarked : prod.marked
    }));
    setMappedProducts(updatedProducts);
  };

  const filterTimingOnSelect = ({ key }) => {
    const keyValue = key === 'none' ? '' : key;
    const updatedProducts = mappedProducts.map(prod => {
      const doesContain =
        keyValue === 'clear' ? prod.marked : keyValue === prod.timingEvent;
      return {
        ...prod,
        hide: key === 'clear' ? false : !doesContain
      };
    });
    setMappedProducts(updatedProducts);
  };

  const filterTimingTagsOnSelect = ({ key }) => {
    const keyValue = key === 'none' ? '' : key;
    const updatedProducts = mappedProducts.map(prod => {
      const doesContain =
        !prod.timingEventTag || keyValue === prod.timingEventTag;
      return {
        ...prod,
        hide: key === 'clear' ? false : !doesContain
      };
    });
    setMappedProducts(updatedProducts);
  };

  useEffect(() => {
    const planProducts = currentPlan?.planProductEntries?.map(planProd => {
      return {
        marked: false,
        hide: false,
        ...planProd
      };
    });
    setMappedProducts(planProducts);

    const newTimings = cloneDeep(baseDropDownValues);
    const combined = newTimings.concat(cloneDeep(timingKeyValuePair));
    setTimings(combined);

    const tempTimingTags = cloneDeep(baseDropDownValues);
    // eslint-disable-next-line no-unused-expressions
    currentPlan?.planProductEntries?.forEach(prod => {
      if (
        prod.timingEventTag &&
        tempTimingTags.findIndex(tag => tag.key === prod.timingEventTag) === -1
      ) {
        tempTimingTags.push({
          key: prod.timingEventTag,
          value: prod.timingEventTag
        });
      }
    });
    setTimingTags(tempTimingTags);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPlan?.planProductEntries]);

  useEffect(() => {
    reloadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal
      open={open}
      onClose={close}
      onCancel={close}
      onConfirm={close}
      size="lg"
      hideClose
    >
      <div
        className="p-5 flex flex-col"
        style={{ maxHeight: '80vh', minHeight: 0 }}
      >
        <div className="text-2xl font-bold mt-2 mb-4">
          Convert to Recommendation
        </div>
        <Formik initialValues={initialValues} onSubmit={() => convertToRec()}>
          {({ values, handleSubmit }) => {
            return (
              <div className="flex flex-col flex-1 min-h-0">
                <div className="flex gap-4 mb-4">
                  <div className="w-1/2">
                    <Form.Field label="Timing">
                      <GrowUIFormField
                        control={Select}
                        value={timingDropValue}
                        items={timings}
                        id="timing"
                        name="timing"
                        onChange={e => {
                          let dValue = null;
                          if (JSON.parse(e.target.value).key !== 'clear') {
                            dValue = JSON.parse(e.target.value).key;
                          }
                          filterTimingOnSelect(JSON.parse(e.target.value));
                          setTimingDropValue(dValue);
                        }}
                      />
                    </Form.Field>
                  </div>
                  <div className="w-1/2">
                    <Form.Field label="Timing Tag">
                      <GrowUIFormField
                        control={Select}
                        selected={values.timingTag}
                        value={timingTagDropValue}
                        items={timingTags}
                        id="timingTag"
                        name="timingTag"
                        onChange={e => {
                          let dValue = null;
                          if (JSON.parse(e.target.value).key !== 'clear') {
                            dValue = JSON.parse(e.target.value).key;
                          }
                          filterTimingTagsOnSelect(JSON.parse(e.target.value));
                          setTimingTagDropValue(dValue);
                        }}
                      />
                    </Form.Field>
                  </div>
                </div>
                <div className="text-2xl font-bold">Products & Services</div>
                <div className="text-sm mb-2">
                  Select the product(s) you want to include in your
                  recommendation
                </div>
                <div className="flex-1 min-h-0 overflow-auto mb-4 border border-solid">
                  <ConvertPlanTable
                    planProducts={mappedProducts}
                    markAll={markUnmarkAll}
                    markUnmarkProduct={markUnmarkProduct}
                    loading={loading}
                    allAreMarked={allAreMarked}
                    id="productsTable"
                    name="productsTable"
                  />
                </div>
                <div className="flex justify-end">
                  <div className="mr-4">
                    <Button
                      onClick={() => {
                        closeModal();
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                  <Button type="primary" onClick={handleSubmit}>
                    Next
                  </Button>
                </div>
              </div>
            );
          }}
        </Formik>
      </div>
    </Modal>
  );
};

ConvertPlanToRecModal.propTypes = {
  open: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  currentPlan: PropTypes.object
};

export default ConvertPlanToRecModal;
